import { useEffect, useRef } from "react";

/**
 * Hook to automatically refresh data when IndexedDB sync completes
 * 
 * @param {Function} refreshCallback - Function to call when sync completes (e.g., fetchData)
 * @param {boolean} enabled - Whether to enable the auto-refresh (default: true)
 * 
 * @example
 * const fetchData = () => { ... };
 * useDataSyncRefresh(fetchData);
 */
export const useDataSyncRefresh = (refreshCallback, enabled = true) => {
  const callbackRef = useRef(refreshCallback);
  const hasListenedRef = useRef(false);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = refreshCallback;
  }, [refreshCallback]);

  useEffect(() => {
    if (!enabled) return;

    const handleSyncComplete = (event) => {
      // Add a small delay to ensure IndexedDB write operations are complete
      setTimeout(() => {
        // Call the latest callback when sync completes
        if (callbackRef.current) {
          callbackRef.current();
        }
      }, 100); // 100ms delay to ensure IndexedDB is ready
    };

    // Check if sync already completed before this component mounted
    // We'll listen for future syncs and also check if we missed one
    const checkRecentSync = () => {
      const lastSyncTime = window.lastDataSyncTime;
      if (lastSyncTime) {
        const timeSinceSync = Date.now() - lastSyncTime;
        // If sync happened within the last 5 seconds, trigger refresh
        if (timeSinceSync < 5000) {
          setTimeout(() => {
            if (callbackRef.current) {
              callbackRef.current();
            }
          }, 200); // Longer delay for missed syncs
        }
      }
      
      // Also check if sync is currently in progress
      // If isSyncingAllData flag exists and is true, wait a bit and check again
      if (window.isSyncingAllData) {
        // The event listener will handle it when sync completes
        // But also set up a polling check in case event is missed
        const checkInterval = setInterval(() => {
          if (!window.isSyncingAllData && window.lastDataSyncTime) {
            clearInterval(checkInterval);
            const timeSinceSync = Date.now() - window.lastDataSyncTime;
            if (timeSinceSync < 1000) {
              setTimeout(() => {
                if (callbackRef.current) {
                  callbackRef.current();
                }
              }, 200);
            }
          }
        }, 100);
        
        // Clear interval after 10 seconds to avoid infinite polling
        setTimeout(() => clearInterval(checkInterval), 10000);
      }
    };

    window.addEventListener('dataSyncComplete', handleSyncComplete);
    
    // Check for recent sync on mount
    if (!hasListenedRef.current) {
      hasListenedRef.current = true;
      checkRecentSync();
    }

    return () => {
      window.removeEventListener('dataSyncComplete', handleSyncComplete);
    };
  }, [enabled]);
};

